import { useEffect, useState, useCallback } from "react";
import { PageTitle } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useDash } from "./ctx";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { ShieldCheck, ShieldAlert, Save, Lock, Radio, Clock, Plus, X, PhoneCall } from "lucide-react";

const TIERS = [
  { k: "T1", label: "Reporting & Alerts", desc: "AI intake + safety alerts. No live agents (honest queue)." },
  { k: "T2", label: "Live-Monitored", desc: "Redline operators watch during covered hours." },
  { k: "T2_24_7", label: "Live-Monitored 24/7", desc: "Round-the-clock live coverage." },
];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function Security() {
  const { tenantId, tenant, role } = useDash();
  const [sm, setSm] = useState(null);
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!tenantId) return;
    const [t, s] = await Promise.all([
      api.get(`/tenants/${tenantId}`),
      api.get(`/tenants/${tenantId}/security/status`),
    ]);
    setSm(t.data.security_module || { enabled: false, tier: "T1", coverage: { timezone: "America/Chicago", is_24_7: false, windows: [] }, on_call_escalation: { enabled: false, notify: [] }, alerts: { enabled: true } });
    setStatus(s.data);
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  if (role !== "super_admin") {
    return (
      <div className="bg-white rounded-2xl border border-sand-200 p-12 text-center max-w-md mx-auto flex flex-col items-center gap-3">
        <Lock className="h-8 w-8 text-sand-300" />
        <p className="font-heading font-bold">Provisioned by Redline</p>
        <p className="text-sm text-muted-foreground">The Security Add-on is a paid service configured by Redline super admins. Contact your Redline rep to enable live monitoring for this community.</p>
      </div>
    );
  }
  if (!sm) return null;

  const cov = sm.coverage || { timezone: "America/Chicago", is_24_7: false, windows: [] };
  const upd = (patch) => setSm({ ...sm, ...patch });
  const updCov = (patch) => setSm({ ...sm, coverage: { ...cov, ...patch } });
  const updWindow = (i, patch) => {
    const windows = [...(cov.windows || [])];
    windows[i] = { ...windows[i], ...patch };
    updCov({ windows });
  };
  const toggleDay = (i, day) => {
    const w = cov.windows[i];
    const days = w.days?.includes(day) ? w.days.filter((d) => d !== day) : [...(w.days || []), day];
    updWindow(i, { days });
  };
  const addWindow = () => updCov({ windows: [...(cov.windows || []), { days: ["Mon", "Tue", "Wed", "Thu", "Fri"], start: "19:00", end: "05:00" }] });
  const removeWindow = (i) => updCov({ windows: cov.windows.filter((_, idx) => idx !== i) });

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/tenants/${tenantId}/security`, sm);
      setStatus(res.data.status);
      toast.success("Security module saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const live = status?.live_now;
  const showWindows = sm.enabled && sm.tier === "T2" && !cov.is_24_7;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="security-page">
      <div className="flex items-start justify-between">
        <div>
          <PageTitle title={<>Security Add-on <span className="text-xs bg-redline-50 text-redline-700 px-2.5 py-1 rounded-full font-bold">Premium</span></>} about={<>Live-monitored security concierge for {tenant?.name}. Configured by Redline.</>} />
        </div>
        <button data-testid="security-save-btn" onClick={save} disabled={saving} className="rounded-full bg-redline-500 text-white font-semibold px-5 py-2.5 flex items-center gap-2 hover:bg-redline-600 transition-colors disabled:opacity-60">
          <Save className="h-4 w-4" strokeWidth={1.5} /> {saving ? "Saving…" : "Save"}
        </button>
      </div>

      {/* live status banner */}
      <div data-testid="security-status-banner" className={`rounded-2xl border p-5 flex items-center gap-4 ${!sm.enabled ? "bg-sand-100 border-sand-200" : live ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
        <div className={`h-11 w-11 rounded-xl bg-white flex items-center justify-center ${!sm.enabled ? "text-ink-400" : live ? "text-emerald-600" : "text-amber-600"}`}>
          {live ? <Radio className="h-6 w-6" /> : <ShieldAlert className="h-6 w-6" />}
        </div>
        <div>
          <div className="font-heading font-bold text-ink">
            {!sm.enabled ? "Module OFF — AI concierge only" : live ? "Live monitoring ACTIVE" : "Live team offline right now"}
          </div>
          <div className="text-sm text-muted-foreground">
            {!sm.enabled ? "No security intake, alerts, or live agents for this community." :
              live ? "A Redline operator is on duty and will see urgent reports." :
                `Reports queue honestly${status?.next_open_str ? ` — team back ${status.next_open_str}` : ""}. Emergencies always routed to 911.`}
          </div>
        </div>
      </div>

      <section className="bg-white rounded-2xl border border-sand-200 p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 font-heading font-bold"><ShieldCheck className="h-4 w-4 text-redline-500" /> Enable Security Add-on</div>
          <Switch data-testid="security-enabled-toggle" checked={!!sm.enabled} onCheckedChange={(v) => upd({ enabled: v })} />
        </div>

        {sm.enabled && (
          <>
            <div>
              <label className="text-sm font-semibold">Service tier</label>
              <div className="mt-2 space-y-2">
                {TIERS.map((t) => (
                  <button key={t.k} data-testid={`security-tier-${t.k}`} onClick={() => upd({ tier: t.k })}
                    className={`w-full text-left rounded-xl border px-4 py-3 transition-colors ${sm.tier === t.k ? "border-ink bg-ink text-white" : "border-sand-200 bg-sand-50 hover:border-ink"}`}>
                    <div className="font-semibold text-sm">{t.label}</div>
                    <div className={`text-xs ${sm.tier === t.k ? "text-sand-200" : "text-muted-foreground"}`}>{t.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {(sm.tier === "T2" || sm.tier === "T2_24_7") && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 font-semibold text-sm"><Clock className="h-4 w-4 text-redline-500" /> Coverage hours</div>
                <div className="flex items-center gap-3">
                  <input data-testid="security-timezone" value={cov.timezone || ""} onChange={(e) => updCov({ timezone: e.target.value })}
                    placeholder="America/Chicago" className="flex-1 rounded-xl border border-sand-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
                  {sm.tier === "T2" && (
                    <label className="flex items-center gap-2 text-sm shrink-0">
                      <Switch data-testid="security-247-toggle" checked={!!cov.is_24_7} onCheckedChange={(v) => updCov({ is_24_7: v })} /> 24/7
                    </label>
                  )}
                </div>

                {showWindows && (
                  <div className="space-y-3">
                    {(cov.windows || []).map((w, i) => (
                      <div key={i} data-testid={`security-window-${i}`} className="rounded-xl border border-sand-200 p-3 space-y-2">
                        <div className="flex flex-wrap gap-1.5">
                          {DAYS.map((d) => (
                            <button key={d} onClick={() => toggleDay(i, d)}
                              className={`text-xs font-semibold px-2.5 py-1 rounded-full border transition-colors ${w.days?.includes(d) ? "bg-ink text-white border-ink" : "bg-sand-50 border-sand-200"}`}>{d}</button>
                          ))}
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <input type="time" value={w.start} onChange={(e) => updWindow(i, { start: e.target.value })} className="rounded-lg border border-sand-200 px-2 py-1" />
                          <span className="text-muted-foreground">to</span>
                          <input type="time" value={w.end} onChange={(e) => updWindow(i, { end: e.target.value })} className="rounded-lg border border-sand-200 px-2 py-1" />
                          <button onClick={() => removeWindow(i)} className="ml-auto text-ink-400 hover:text-redline-600"><X className="h-4 w-4" /></button>
                        </div>
                        <p className="text-[11px] text-muted-foreground">Overnight windows (e.g. 19:00 → 05:00) are supported.</p>
                      </div>
                    ))}
                    <button data-testid="security-add-window" onClick={addWindow} className="text-sm font-semibold text-redline-600 flex items-center gap-1 hover:text-redline-700">
                      <Plus className="h-4 w-4" /> Add coverage window
                    </button>
                  </div>
                )}

                <div className="flex items-center justify-between pt-2 border-t border-sand-100">
                  <div className="flex items-center gap-2 text-sm"><PhoneCall className="h-4 w-4 text-redline-500" /> Off-hours on-call escalation</div>
                  <Switch data-testid="security-oncall-toggle" checked={!!sm.on_call_escalation?.enabled}
                    onCheckedChange={(v) => upd({ on_call_escalation: { ...(sm.on_call_escalation || {}), enabled: v } })} />
                </div>
                {sm.on_call_escalation?.enabled && (
                  <input data-testid="security-oncall-notify" value={(sm.on_call_escalation?.notify || []).join(", ")}
                    onChange={(e) => upd({ on_call_escalation: { ...sm.on_call_escalation, notify: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) } })}
                    placeholder="On-call patrol phone/email, comma-separated" className="w-full rounded-xl border border-sand-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
                )}
              </div>
            )}

            <div className="flex items-center justify-between pt-3 border-t border-sand-100">
              <div className="text-sm font-semibold">Community safety broadcasts</div>
              <Switch data-testid="security-alerts-toggle" checked={!!sm.alerts?.enabled}
                onCheckedChange={(v) => upd({ alerts: { ...(sm.alerts || {}), enabled: v } })} />
            </div>
          </>
        )}
      </section>

      <p className="text-xs text-muted-foreground">Emergencies are always routed to 911 regardless of tier or hours. Incidents captured by the concierge appear in <span className="font-semibold">Intake Actions</span>.</p>
    </div>
  );
}
